/**
 * Inventory Packages — PRD.260
 *
 * Read-side queries for package composition. A package is an `inventory_items`
 * row with `item_type = 'package'`; its included items live in
 * `inventory_package_items`, one active row per child inventory item.
 *
 * Boundaries (do NOT change without a new PRD):
 *   - Read-only against inventory_* tables. Writes (create/replace/soft-delete)
 *     are performed by the inventory create/edit page via `db.execute(...)` so
 *     PowerSync records CRUD entries and syncs them.
 *   - No stock deduction and no patient package / redemption records — those
 *     belong to a future redemption PRD.
 */

import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Types ───

/**
 * A raw `inventory_package_items` row (SQLite representation).
 * `deleted_at` is null for active rows.
 */
export interface InventoryPackageItemRow {
  id: string;
  org_id: string | null;
  package_inventory_item_id: string | null;
  child_inventory_item_id: string | null;
  quantity: number | null;
  included_unit_price: number | null;
  sort_order: number | null;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
}

/**
 * A package composition row joined with its child inventory item so the
 * builder can display the child's name, type, and current selling price
 * without a second query. Only active (non-deleted) rows are returned.
 */
export interface PackageIncludedItemRow {
  id: string; // inventory_package_items.id
  package_inventory_item_id: string;
  child_inventory_item_id: string;
  quantity: number | null;
  included_unit_price: number | null;
  sort_order: number | null;
  // Child inventory item display fields
  child_item_name: string | null;
  child_sku: string | null;
  child_item_type: string | null;
  child_selling_price: number | null;
  child_inventory_unit: string | null;
}

/**
 * A candidate row for the package item picker — a non-package inventory item
 * eligible to be added to a package.
 */
export interface PackageChildCandidateRow {
  id: string;
  item_name: string | null;
  sku: string | null;
  item_type: string | null;
  selling_price: number | null;
  inventory_unit: string | null;
  category: string | null;
  status: string | null;
  inactive: number | null;
}

// ─── Queries ───

/**
 * List a package's active included items joined with each child inventory
 * item, ordered by sort_order then name. Excludes soft-deleted rows.
 */
export function qInventoryPackageItems(packageId: string): QuerySpec<[string]> {
  return {
    key: ['inventory-package-items', packageId] as const,
    sql: `
      SELECT
        p.id                         AS id,
        p.package_inventory_item_id  AS package_inventory_item_id,
        p.child_inventory_item_id    AS child_inventory_item_id,
        p.quantity                   AS quantity,
        p.included_unit_price        AS included_unit_price,
        p.sort_order                 AS sort_order,
        c.item_name                  AS child_item_name,
        c.sku                        AS child_sku,
        c.item_type                  AS child_item_type,
        c.selling_price              AS child_selling_price,
        c.inventory_unit             AS child_inventory_unit
      FROM inventory_package_items p
      LEFT JOIN inventory_items c ON c.id = p.child_inventory_item_id
      WHERE p.package_inventory_item_id = ?
        AND p.deleted_at IS NULL
      ORDER BY p.sort_order ASC, c.item_name ASC
    `,
    params: [packageId],
    watch: ['inventory_package_items', 'inventory_items'],
  };
}

/**
 * Search inventory items eligible to be added to a package: non-package,
 * active items in the given org, excluding the package being edited. Empty
 * `search` returns the unfiltered (capped) list. Result count capped at 50.
 */
export function qPackageChildCandidates(
  orgId: string,
  search: string,
  excludePackageId: string,
): QuerySpec<unknown[]> {
  const term = `%${search.trim().toLowerCase()}%`;
  return {
    key: ['package-child-candidates', orgId, search, excludePackageId] as const,
    sql: `
      SELECT
        i.id, i.item_name, i.sku, i.item_type, i.selling_price,
        i.inventory_unit, i.category, i.status, i.inactive
      FROM inventory_items i
      WHERE i.org_id = ?
        AND i.item_type != 'package'
        AND i.id != ?
        AND COALESCE(i.inactive, 0) = 0
        AND COALESCE(i.status, 'Active') != 'Inactive'
        AND (
          ? = '%%'
          OR LOWER(COALESCE(i.item_name, '')) LIKE ?
          OR LOWER(COALESCE(i.sku, ''))       LIKE ?
        )
      ORDER BY i.item_name ASC
      LIMIT 50
    `,
    params: [orgId, excludePackageId, term, term, term],
    watch: ['inventory_items'],
  };
}
